// features/workstations/dashboard-sections/dte-conditions.js
// ═══════════════════════════════════════════════════════════
// 0DTE CONDITIONS — day-of context for 0DTE trading
// ═══════════════════════════════════════════════════════════
// Tier D day-trading. V1 ships with what’s free: session
// timing, VIX term structure, CBOE PCR, spot prints, today’s
// macro events. Pin levels + gamma walls + dealer GEX render
// as labeled placeholders pending V2 paid-data wiring (best
// fit: SpotGamma for pure gamma mechanics, Unusual Whales for
// flow + GEX value).
// ═══════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from 'react';
import {
  Eyebrow,
  SerifHeadline,
  SerifSummary,
  SectionStrip,
  PillRow,
  PillTable,
  PractitionerLens,
  MetaField,
} from '../../../components/widgets/primitives';

const PHASE_TONES = {
  pre_market:   'steel',
  opening:      'amber',
  mid:          'sage',
  closing:      'terra',
  after_hours:  'steel',
  closed:       'steel',
};

// ═══════════════════════════════════════════════════════════
// SECTION
// ═══════════════════════════════════════════════════════════
export default function DteConditionsSection({ workstation, userId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!workstation?.id || !userId) return;
    setLoading(true); setError(null);
    try {
      const qs = new URLSearchParams({
        workstation_id: workstation.id,
        user_id: userId,
      });
      const res = await fetch(`/api/workstations/dte-conditions?${qs}`, { cache: 'no-store' });
      const payload = res.ok ? await res.json() : null;
      if (!res.ok) setError(payload?.error || `HTTP ${res.status}`);
      setData(payload || null);
    } catch (e) {
      setError(e?.message || 'Load failed');
    } finally {
      setLoading(false);
    }
  }, [workstation?.id, userId]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 5 min during market hours
  useEffect(() => {
    if (!data?.session?.is_market_hours) return;
    const id = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [data?.session?.is_market_hours, load]);

  if (loading) {
    return (
      <section className="ws-dte">
        <div className="ws-dte-loading">Loading 0DTE conditions…</div>
      </section>
    );
  }

  if (error || !data) {
    return (
      <section className="ws-dte">
        <Eyebrow>0dte conditions — read failed</Eyebrow>
        <SerifSummary>{error || 'No data returned.'}</SerifSummary>
      </section>
    );
  }

  return <DteBody data={data} />;
}

// ═══════════════════════════════════════════════════════════
// BODY
// ═══════════════════════════════════════════════════════════
function DteBody({ data }) {
  const session = data.session || {};
  const vol = data.volatility || {};
  const pcr = data.pcr || null;
  const spot = data.spot || {};
  const events = Array.isArray(data.events_today) ? data.events_today : [];

  const phase = session.phase || 'closed';
  const phaseTone = PHASE_TONES[phase] || 'steel';
  const phaseLabel = session.label || 'Closed';

  const headline = phaseHeadline(session, vol, events);
  const headlineTone = phaseHeadlineTone(session, vol);

  const summary = phaseSummary(session, vol, events);

  return (
    <section className="ws-dte">
      <Eyebrow parts={[
        '0dte conditions',
        session.et_clock ? `${session.et_clock} ET` : null,
        phaseLabel.toLowerCase(),
      ]} />

      <SerifHeadline>
        <span className={`ws-dte-tone-${headlineTone}`}>{headline}</span>
      </SerifHeadline>
      <SerifSummary>{summary}</SerifSummary>

      {/* SESSION CLOCK */}
      <SectionStrip label="session clock">
        <div className="ws-dte-clock">
          <div className={`ws-dte-phase ws-dte-phase-${phaseTone}`}>{phaseLabel}</div>
          {session.minutes_to_close != null && session.minutes_to_close >= 0 && (
            <MetaField label="minutes to close">{session.minutes_to_close}</MetaField>
          )}
          {session.minutes_to_phase_end != null && session.minutes_to_phase_end > 0 && (
            <MetaField label="phase ends in">{session.minutes_to_phase_end}m</MetaField>
          )}
        </div>
      </SectionStrip>

      {/* VOLATILITY */}
      {vol.grid?.length > 0 && (
        <SectionStrip
          label={vol.term_structure ? `volatility · ${vol.term_structure}` : 'volatility'}
          count={vol.grid.length}
        >
          <PillTable>
            {vol.grid.map((row, i) => (
              <PillRow
                key={i}
                keyText={row.label || ''}
                valueText={`${row.value || ''}${row.change ? ` · ${row.change}` : ''}`}
                tone={row.dir === 'up' ? 'terra' : row.dir === 'dn' ? 'sage' : 'steel'}
              />
            ))}
          </PillTable>
          {vol.caution_flag && (
            <PractitionerLens label="0dte caution">
              VIX term structure is in <strong>backwardation</strong> — near-term IV
              elevated relative to longer-dated. 0DTE premiums are rich; long
              gamma is expensive, short gamma carries tail risk. Trade smaller.
            </PractitionerLens>
          )}
        </SectionStrip>
      )}

      {/* PCR (sentiment proxy) */}
      {pcr && (
        <SectionStrip label={`cboe pcr · ${pcr.report_date || 'latest'}`}>
          <PillTable>
            {pcr.total_pcr != null && (
              <PillRow
                keyText="Total PCR"
                valueText={`${pcr.total_pcr.toFixed(2)} · ${pcrInterpretation(pcr.total_pcr)}`}
                tone={pcrTone(pcr.total_pcr)}
              />
            )}
            {pcr.equity_pcr != null && (
              <PillRow
                keyText="Equity PCR"
                valueText={`${pcr.equity_pcr.toFixed(2)} · ${pcrInterpretation(pcr.equity_pcr)}`}
                tone={pcrTone(pcr.equity_pcr)}
              />
            )}
            {pcr.index_pcr != null && (
              <PillRow
                keyText="Index PCR"
                valueText={pcr.index_pcr.toFixed(2)}
                tone="steel"
              />
            )}
          </PillTable>
        </SectionStrip>
      )}

      {/* SPOT (the day-trading indices) */}
      {Object.keys(spot).length > 0 && (
        <SectionStrip label="spot">
          <PillTable>
            {['SPY', 'QQQ', 'IWM', '^VIX'].map((t) => {
              const q = spot[t];
              if (!q || q.price == null) return null;
              const pct = q.change_pct;
              const tone = pct > 0 ? 'sage' : pct < 0 ? 'terra' : 'steel';
              return (
                <PillRow
                  key={t}
                  keyText={t.replace('^', '')}
                  valueText={`$${q.price.toFixed(2)}${pct != null ? ` · ${pct >= 0 ? '+' : ''}${pct.toFixed(2)}%` : ''}`}
                  tone={tone}
                />
              );
            })}
          </PillTable>
        </SectionStrip>
      )}

      {/* MACRO EVENTS TODAY */}
      {events.length > 0 && (
        <SectionStrip label="high-impact today" count={events.length}>
          <div className="ws-dte-events">
            {events.map((e, i) => (
              <div key={i} className="ws-dte-event">
                <span className="ws-dte-event-time">{e.time || ''}</span>
                <span className="ws-dte-event-name">{e.name || ''}</span>
              </div>
            ))}
          </div>
        </SectionStrip>
      )}

      {/* V2 PLACEHOLDER — paid-data layer */}
      <SectionStrip label="pin levels & gamma (paid data — not wired)">
        <div className="ws-dte-v2-placeholder">
          <div className="ws-dte-v2-cards">
            {['Pin levels (max-pain strikes)', 'Gamma walls (call wall / put wall)', 'Dealer net GEX', 'Charm flow (intraday hedging)'].map((label) => (
              <div key={label} className="ws-dte-v2-card">
                <div className="ws-dte-v2-card-label">{label}</div>
                <div className="ws-dte-v2-card-stub">—</div>
              </div>
            ))}
          </div>
          <div className="ws-dte-v2-note">
            Wire one of: <strong>SpotGamma</strong> (best for pure gamma mechanics) ·{' '}
            <strong>Unusual Whales</strong> (best value — flow + GEX + dark pool) ·{' '}
            <strong>Tier1Alpha</strong> (most institutional). API hooks land in V2.
          </div>
        </div>
      </SectionStrip>

      <PractitionerLens label="what V1 covers — honestly">
        Session timing, IV term structure, CBOE PCR, spot prints, macro
        events. The structural gamma view (pin levels, dealer flip,
        charm) requires paid options-flow data and ships in V2. Don’t
        trade 0DTE based on stale or inferred gamma data — better to
        flag the absence than fake the precision.
      </PractitionerLens>
    </section>
  );
}

// ─── Headline + summary helpers ────────────────────────
function phaseHeadline(session, vol, events) {
  const phase = session.phase;
  if (phase === 'closed') return 'Market closed.';
  if (phase === 'pre_market') {
    if (events.length > 0) return `Pre-market · ${events.length} high-impact event${events.length === 1 ? '' : 's'} today.`;
    return 'Pre-market — prepare the day.';
  }
  if (phase === 'opening') {
    if (vol.caution_flag) return 'Opening hour · IV backwardation — size down.';
    return 'Opening hour — highest 0DTE decay window.';
  }
  if (phase === 'mid') return `Mid-session${session.minutes_to_close ? ` · ${session.minutes_to_close}m to close.` : '.'}`;
  if (phase === 'closing') return `Final hour${session.minutes_to_close ? ` · ${session.minutes_to_close}m left` : ''} — gamma squeeze risk.`;
  if (phase === 'after_hours') return 'After-hours — 0DTE expired.';
  return 'Session.';
}

function phaseHeadlineTone(session, vol) {
  if (session.phase === 'closing' || vol.caution_flag) return 'terra';
  if (session.phase === 'opening') return 'amber';
  if (session.phase === 'mid') return 'sage';
  return 'steel';
}

function phaseSummary(session, vol, events) {
  if (session.phase === 'closed') return 'Use the time to plan tomorrow. Nothing to trade until 4am ET.';
  if (session.phase === 'pre_market') return 'Volume is thin and gaps lie. Watch the open for confirmation.';
  if (session.phase === 'opening') return 'First 30 minutes carry the most theta + IV crush. Most edges fade after 10am ET.';
  if (session.phase === 'mid') return 'Slowest part of the day for 0DTE — unless a catalyst hits, expect chop.';
  if (session.phase === 'closing') return 'Dealer hedging accelerates. Pin levels matter most here — V2 will surface them.';
  if (session.phase === 'after_hours') return 'Equity options closed. ES futures continue if you’re hedging overnight risk.';
  return '';
}

function pcrInterpretation(pcr) {
  if (pcr >= 1.30) return 'extreme fear (contrarian bullish)';
  if (pcr >= 0.90) return 'elevated puts (bearish lean)';
  if (pcr >= 0.70) return 'neutral';
  return 'extreme complacency (contrarian bearish)';
}

function pcrTone(pcr) {
  if (pcr >= 1.30) return 'terra';
  if (pcr >= 0.90) return 'amber';
  if (pcr >= 0.70) return 'steel';
  return 'gold';
}
